// src/quiz/gre-mock8/verbalData.js
// GRE Mock 8 — Verbal Reasoning.
// Section 1: questions 1–12 (18 min). Section 2: questions 13–27 (23 min).
// Question format: see ../greQuestionTypes.js

const SE_INSTRUCTION = "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.";

const SCRIBES_PASSAGE =
  "In the decades after the invention of the printing press, scribes did not vanish, as many historians have assumed. Instead, many copyists found new work producing hand-decorated manuscripts for wealthy patrons who wanted objects that mass-produced books could not supply. Printed books were cheap and uniform; a handwritten manuscript, by contrast, advertised the owner's wealth and taste. This shift suggests that new technologies often do not eliminate older crafts but push them toward a different kind of value, one based on distinction rather than efficiency. Some scholars, however, caution that the evidence for this view comes mainly from a few surviving collections, which may not be representative of the broader market.";

const BIRDS_PASSAGE =
  "Researchers studying songbirds in several large cities have found that urban birds often sing at a higher pitch than their rural relatives. The standard explanation holds that low-frequency traffic noise masks lower notes, so birds that sing higher are heard more clearly and therefore reproduce more successfully. A competing hypothesis proposes that the change is not an adaptation at all, but a temporary adjustment: individual birds raise their pitch when noise levels rise and lower it again when they fall. Evidence for this second view comes from experiments in which a single bird changed its pitch within minutes of hearing recorded traffic. Yet the two explanations are not mutually exclusive. Temporary adjustment could be the first step in a longer process: if birds that adjust well raise more chicks, and those chicks inherit or learn the higher songs, then a short-term behavior could eventually become a population-wide trait. Distinguishing between these possibilities will require tracking the same populations across many generations, a task that few studies have attempted.";

const verbalQuestions = [

  // ---------- SECTION 1 ----------

  {
    id: 1,
    question: "The mayor's ______ response to the crisis, issued only after weeks of silence, did little to reassure residents.",
    options: ["prompt", "belated", "vigorous", "candid", "spontaneous"],
    answer: "belated",
    explanation: "A response \"issued only after weeks of silence\" came late: belated. Prompt and spontaneous say the opposite, and vigorous or candid don't explain why residents stayed unconvinced.",
  },
  {
    id: 2,
    question: "Although the novelist was widely thought to be reclusive, she was in fact quite ______, hosting lively gatherings at her home every weekend.",
    options: ["sociable", "austere", "taciturn", "pensive", "fastidious"],
    answer: "sociable",
    explanation: "\"Although\" sets up a contrast with reclusive, and the weekly gatherings confirm it: she was sociable. Taciturn (quiet) and austere would agree with the reclusive image.",
  },
  {
    id: 3,
    question: "The new software update was virtually ______, since users noticed no difference in speed or appearance.",
    options: ["revolutionary", "imperceptible", "disruptive", "contentious", "exorbitant"],
    answer: "imperceptible",
    explanation: "If users noticed no difference at all, the update could hardly be perceived: imperceptible. Revolutionary and disruptive imply a change people would notice.",
  },
  {
    id: 4,
    question: "Critics praised the documentary for its ______, presenting both the company's achievements and its failures without bias.",
    options: ["impartiality", "zealotry", "brevity", "pomp", "nostalgia"],
    answer: "impartiality",
    explanation: "Showing both successes and failures \"without bias\" is the definition of impartiality. Zealotry is the opposite—fanatical one-sidedness.",
  },
  {
    id: 5,
    question: "The engineer's ______ attention to detail meant that not a single error escaped her review.",
    options: ["cursory", "meticulous", "fitful", "grudging", "lavish"],
    answer: "meticulous",
    explanation: "Catching every single error requires extremely careful attention: meticulous. Cursory (hasty) and fitful (irregular) would let errors through.",
  },
  {
    id: 6,
    question: "Because the treaty's wording was deliberately ______, each side could claim it had won a concession.",
    options: ["precise", "ambiguous", "exhaustive", "rigid", "emphatic"],
    answer: "ambiguous",
    explanation: "Both sides can read a win into the text only if it supports more than one interpretation: ambiguous. Precise or rigid wording would settle the question for one side.",
  },
  {
    id: 7,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The coach's praise was ______; she rarely complimented a player unless the performance was truly exceptional.",
    options: ["restrained", "lavish", "sparing", "boundless", "indiscriminate", "fervent"],
    answer: ["restrained", "sparing"],
    explanation: "Praise given rarely and only for exceptional work is held back: restrained and sparing. Lavish and boundless form a pair too, but they contradict \"rarely complimented.\"",
  },
  {
    id: 8,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The diplomat's ______ manner calmed the tense negotiators and helped the talks resume.",
    options: ["soothing", "conciliatory", "belligerent", "truculent", "aloof", "detached"],
    answer: ["soothing", "conciliatory"],
    explanation: "A manner that calms tension and restarts talks is soothing or conciliatory (seeking to placate). Belligerent and truculent are a pair meaning aggressive—the opposite effect.",
  },
  {
    id: 9,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Because the evidence was ______, the jury hesitated to convict, wanting stronger proof.",
    options: ["scant", "ample", "meager", "copious", "irrelevant", "decisive"],
    answer: ["scant", "meager"],
    explanation: "The jury wanted \"stronger proof,\" so the evidence was thin: scant or meager. Ample and copious pair up, but plentiful evidence would not leave the jury wanting more.",
  },
  {
    id: 10,
    passage: SCRIBES_PASSAGE,
    question: "The passage is primarily concerned with",
    options: [
      "arguing that printing presses were less efficient than scribes",
      "describing how scribes' work changed rather than disappeared after printing, while noting a limit on the evidence",
      "criticizing historians for ignoring wealthy patrons",
      "explaining how mass-produced books were manufactured",
      "proving that handwritten manuscripts were superior in quality",
    ],
    answer: "describing how scribes' work changed rather than disappeared after printing, while noting a limit on the evidence",
    explanation: "The passage says scribes shifted to luxury manuscripts instead of vanishing, draws a general lesson, then notes the evidence may be unrepresentative. Only B covers both the main claim and the caveat.",
  },
  {
    id: 11,
    passage: SCRIBES_PASSAGE,
    question: "According to the passage, wealthy patrons valued handwritten manuscripts because they",
    options: [
      "were cheaper to produce",
      "were more accurate than printed books",
      "signaled the owner's wealth and taste",
      "could be produced faster",
      "were required for official documents",
    ],
    answer: "signaled the owner's wealth and taste",
    explanation: "The passage states directly that a handwritten manuscript \"advertised the owner's wealth and taste.\" Printed books, not manuscripts, were the cheap option.",
  },
  {
    id: 12,
    passage: SCRIBES_PASSAGE,
    question: "The author mentions \"a few surviving collections\" in order to",
    options: [
      "suggest that the conclusion may rest on unrepresentative evidence",
      "prove that scribes' output was small",
      "show that most manuscripts have been destroyed",
      "support the claim that printing was inefficient",
      "identify the wealthiest patrons",
    ],
    answer: "suggest that the conclusion may rest on unrepresentative evidence",
    explanation: "The phrase appears in the scholars' caution that the evidence \"may not be representative of the broader market\"—a limit on the conclusion, not a new claim about output or destruction.",
  },

  // ---------- SECTION 2 ----------

  {
    id: 13,
    question: "The senator's reputation for ______ was well earned: she never changed her position, even when evidence mounted against it.",
    options: ["flexibility", "obduracy", "humility", "candor", "charisma"],
    answer: "obduracy",
    explanation: "The colon explains the blank: refusing to change position even against mounting evidence is obduracy (stubbornness). Flexibility is the opposite.",
  },
  {
    id: 14,
    question: "The museum's director, rather than offering a ______ account of the theft, chose to share only a few carefully selected details.",
    options: ["comprehensive", "dubious", "fleeting", "biased", "fictional"],
    answer: "comprehensive",
    explanation: "\"Rather than\" contrasts the blank with sharing only a few details, so the blank means complete: comprehensive.",
  },
  {
    id: 15,
    question: "Despite its ______ appearance, the old bridge was engineered so well that it carried heavy traffic for over a century.",
    options: ["sturdy", "delicate", "imposing", "ornate", "gaudy"],
    answer: "delicate",
    explanation: "\"Despite\" signals a contrast with the bridge's great strength, so it must have looked fragile: delicate. Sturdy and imposing would agree with its strength rather than contrast with it.",
  },
  {
    id: 16,
    question: "Her ______ at the news was obvious; she dropped the phone and stared at the wall without speaking.",
    options: ["consternation", "indifference", "jubilation", "smugness", "serenity"],
    answer: "consternation",
    explanation: "Dropping the phone and staring speechlessly shows shock and dismay: consternation. Indifference and serenity contradict the reaction; jubilation would not leave her silent and stunned.",
  },
  {
    id: 17,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The professor's lectures were so ______ that students often left the room unsure what the main point had been.",
    options: ["lucid", "diffuse", "rambling", "incisive", "succinct", "pointed"],
    answer: ["diffuse", "rambling"],
    explanation: "Students missing the main point suggests unfocused lectures: diffuse and rambling. Lucid, incisive, succinct and pointed all describe clear, focused speaking.",
  },
  {
    id: 18,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The painter, accustomed to obscurity, was ______ by the sudden attention and soon began declining interviews.",
    options: ["unnerved", "unsettled", "flattered", "honored", "amused", "emboldened"],
    answer: ["unnerved", "unsettled"],
    explanation: "Declining interviews shows the attention was unwelcome: unnerved and unsettled. Flattered and honored form a pair, but a flattered painter would welcome interviews.",
  },
  {
    id: 19,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Because the council's budget proposal was ______, many members demanded a detailed breakdown before voting.",
    options: ["opaque", "vague", "itemized", "transparent", "generous", "modest"],
    answer: ["opaque", "vague"],
    explanation: "Members demand a detailed breakdown when the proposal is unclear: opaque and vague. Itemized and transparent would make the demand unnecessary.",
  },
  {
    id: 20,
    passage: BIRDS_PASSAGE,
    question: "The primary purpose of the passage is to",
    options: [
      "present two explanations for a phenomenon and suggest they may be complementary",
      "refute the traffic-noise explanation of birdsong",
      "argue that urban birds are more successful than rural birds",
      "describe the design of a particular set of experiments",
      "criticize researchers for neglecting rural populations",
    ],
    answer: "present two explanations for a phenomenon and suggest they may be complementary",
    explanation: "The passage lays out the adaptation and temporary-adjustment explanations, then says they \"are not mutually exclusive\" and could work together. It refutes neither.",
  },
  {
    id: 21,
    passage: BIRDS_PASSAGE,
    question: "According to the passage, the standard explanation holds that higher-pitched songs are advantageous because they",
    options: [
      "attract more mates regardless of noise",
      "are less likely to be masked by traffic noise",
      "require less energy to produce",
      "are learned more easily by chicks",
      "travel farther in rural areas",
    ],
    answer: "are less likely to be masked by traffic noise",
    explanation: "The standard explanation: low-frequency traffic noise masks lower notes, so higher songs are heard more clearly. That is B.",
  },
  {
    id: 22,
    passage: BIRDS_PASSAGE,
    question: "Which of the following, if true, would most weaken the competing hypothesis described in the passage?",
    options: [
      "Traffic noise levels in cities vary considerably throughout the day.",
      "Rural birds also raise their pitch when exposed to recorded traffic.",
      "Urban birds moved to quiet forests keep singing at the higher pitch for the rest of their lives.",
      "Some urban birds do not sing at all during rush hour.",
      "Urban birds begin nesting earlier in the year than rural birds.",
    ],
    answer: "Urban birds moved to quiet forests keep singing at the higher pitch for the rest of their lives.",
    explanation: "The competing hypothesis says birds lower their pitch again when noise falls. If urban birds in quiet forests keep the high pitch for life, the change is not temporary. B would actually support the hypothesis.",
  },
  {
    id: 23,
    passage: BIRDS_PASSAGE,
    question: "The author's attitude toward the two explanations is best described as",
    options: [
      "strongly in favor of the first",
      "dismissive of both",
      "open to the possibility that both contribute",
      "indifferent to which is correct",
      "alarmed by the lack of evidence",
    ],
    answer: "open to the possibility that both contribute",
    explanation: "The author notes the explanations \"are not mutually exclusive\" and sketches how both could operate, while calling for further study—open-minded, not dismissive or indifferent.",
  },
  {
    id: 24,
    passage: "A city plans to reduce traffic congestion by adding a new lane to its main highway. Planners argue that commute times will fall because the cars will be spread over more road space.",
    question: "Which of the following, if true, most seriously weakens the planners' argument?",
    options: [
      "The new lane can be built in under a year.",
      "In similar cities, added lanes attracted so many new drivers that congestion returned to its former level within a few years.",
      "Some drivers prefer to use the left lane.",
      "The highway has been in use for thirty years.",
      "Public transit fares have increased in recent months.",
    ],
    answer: "In similar cities, added lanes attracted so many new drivers that congestion returned to its former level within a few years.",
    explanation: "The argument assumes the number of cars stays the same. B shows that extra road space draws extra drivers, wiping out the benefit (induced demand).",
  },
  {
    id: 25,
    passage: "Bakery owner: Customers who sample a free piece of bread are far more likely to buy a loaf than customers who do not. Therefore, if I offer free samples to every customer, my loaf sales will increase substantially.",
    question: "The owner's conclusion depends on which of the following assumptions?",
    options: [
      "Samples cost the bakery less than a loaf does.",
      "The customers who currently take samples are not simply the ones who were already most inclined to buy bread.",
      "The bakery sells products other than bread.",
      "Free samples are common in grocery stores.",
      "The owner could stop offering samples at any time.",
    ],
    answer: "The customers who currently take samples are not simply the ones who were already most inclined to buy bread.",
    explanation: "The owner treats a correlation as cause. If sample-takers were simply keen bread buyers anyway, giving samples to everyone would change little. The conclusion needs B to be true.",
  },
  {
    id: 26,
    passage: "Since a new safety helmet was introduced in a certain city, the number of reported head injuries among cyclists has risen. Yet laboratory tests have repeatedly shown that the helmet protects against head injuries.",
    question: "Which of the following, if true, most helps to resolve the apparent discrepancy?",
    options: [
      "Many cyclists still prefer older models of helmet.",
      "Since the helmet was introduced, the number of cyclists in the city has tripled, while the injury rate per cyclist has fallen.",
      "The helmet is more expensive than earlier models.",
      "Head injuries among pedestrians have also been reported.",
      "The laboratory tests were conducted by the manufacturer.",
    ],
    answer: "Since the helmet was introduced, the number of cyclists in the city has tripled, while the injury rate per cyclist has fallen.",
    explanation: "A rising total can coexist with better protection if many more people are cycling. B shows the rate per cyclist fell, so the helmet can work even though the count went up.",
  },
  {
    id: 27,
    passage: "A school principal argues that because test scores rose in the year after a new reading program was introduced, the program caused the improvement.",
    question: "In evaluating the principal's argument, it would be most useful to determine which of the following?",
    options: [
      "Whether the reading program costs more than the program it replaced",
      "Whether test scores rose by a similar amount at schools that did not adopt the program",
      "Whether most teachers enjoy using the new program",
      "How many students attend the principal's school",
      "Whether the program was designed by a former teacher",
    ],
    answer: "Whether test scores rose by a similar amount at schools that did not adopt the program",
    explanation: "Schools without the program act as a control group. If their scores rose just as much, something else (an easier test, a general trend) explains the rise; if not, the program looks responsible.",
  },
];

export default verbalQuestions;
